import { format as formatDate, formatDistanceToNow, parseISO } from "date-fns";
import { Tooltip, type TooltipProps } from "@avenue-8/sidekick-ui";

const DEFAULT_FORMAT = "MMM d, yyyy";
// e.g. "2026-07-15" — a date with no time-of-day component, as opposed to a
// full ISO datetime like "2026-07-15T00:00:00.000Z".
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function toDate(value: string | Date): Date {
  return typeof value === "string" ? parseISO(value) : value;
}

type FormattedDateProps = {
  value: string | Date;
  relative?: boolean;
  placement?: TooltipProps["placement"];
  format?: string;
};

export const FormattedDate = ({
  value,
  relative = false,
  placement = "right",
  format = DEFAULT_FORMAT,
}: FormattedDateProps) => {
  const date = toDate(value);
  const isDateOnly = typeof value === "string" && DATE_ONLY_PATTERN.test(value);
  const absolute = formatDate(date, format);

  // Without a time-of-day component, relative phrasing ("3 hours ago") would
  // claim precision we don't have — fall back to the plain date instead.
  if (isDateOnly) {
    return <span style={{ display: "inline-block" }}>{absolute}</span>;
  }

  const relativeStr = formatDistanceToNow(date, { addSuffix: true });
  const display = relative ? relativeStr : absolute;
  const tooltip = relative ? absolute : relativeStr;

  return (
    <Tooltip title={tooltip} placement={placement}>
      <span style={{ display: "inline-block" }}>{display}</span>
    </Tooltip>
  );
};
